import { Result, Schema } from "effect";
import { CanonicalArray, CanonicalArrayKey, CanonicalDataProperty, CanonicalObject, CanonicalPrototype, CanonicalScalar,
  canonicalInputError, canonicalRefusal, type CanonicalJsonError } from "./model.ts";

const childPath = (path: string, key: string | number) => typeof key === "number" ? `${path}[${key}]` : `${path}[${JSON.stringify(key)}]`;
const inspect = <A>(body: () => A) => Result.try({ try: body, catch: canonicalInputError });
const refuse = (path: string, reason: string) => Result.fail(canonicalRefusal(path, reason));

const serialize = (value: unknown, path: string, ancestors: Set<object>): Result.Result<string, CanonicalJsonError> => Result.gen(function* () {
  if (Schema.is(CanonicalScalar)(value)) return JSON.stringify(value);
  if (Schema.is(Schema.Number)(value)) {
    if (!Schema.is(Schema.Finite)(value)) return yield* refuse(path, "must contain a finite number");
    return JSON.stringify(Object.is(value, -0) ? 0 : value);
  }
  if (!Schema.is(CanonicalObject)(value)) {
    const kind = Schema.is(Schema.Undefined)(value) ? "undefined" : Schema.is(Schema.BigInt)(value) ? "bigint" : Schema.is(Schema.Symbol)(value) ? "symbol" : "function";
    return yield* refuse(path, `contains unsupported ${kind}`);
  }
  // Null has already returned through the scalar boundary.
  const object = value as object;
  if (ancestors.has(object)) return yield* refuse(path, "contains a cycle");
  ancestors.add(object);
  const result = inspect(() => serializeObject(object, path, ancestors)).pipe(Result.flatMap(value => value));
  ancestors.delete(object);
  return yield* result;
});

const serializeObject = (value: object, path: string, ancestors: Set<object>): Result.Result<string, CanonicalJsonError> => Result.gen(function* () {
  if (Schema.is(CanonicalArray)(value)) {
    const descriptors = Object.getOwnPropertyDescriptors(value);
    for (const key of Reflect.ownKeys(value)) {
      if (key === "length") continue;
      if (!Schema.is(CanonicalArrayKey)(key) || Number(key) >= value.length) return yield* refuse(path, "contains a non-JSON array property");
    }
    const items: string[] = [];
    for (let index = 0; index < value.length; index++) {
      const descriptor = descriptors[index];
      if (!descriptor) return yield* refuse(childPath(path, index), "must not be a sparse array element");
      if (!Schema.is(CanonicalDataProperty)(descriptor)) return yield* refuse(childPath(path, index), "must be an enumerable data property");
      items.push(yield* serialize(descriptor.value, childPath(path, index), ancestors));
    }
    return `[${items.join(",")}]`;
  }
  if (!Schema.is(CanonicalPrototype)(Object.getPrototypeOf(value))) return yield* refuse(path, "must be a plain JSON object");
  const descriptors = Object.getOwnPropertyDescriptors(value), keys: string[] = [];
  for (const key of Reflect.ownKeys(descriptors)) {
    if (!Schema.is(Schema.String)(key)) return yield* refuse(path, "contains a symbol property");
    if (!Schema.is(CanonicalDataProperty)(descriptors[key])) return yield* refuse(childPath(path, key), "must be an enumerable data property");
    keys.push(key);
  }
  keys.sort();
  let refusal: CanonicalJsonError | undefined;
  const items = yield* Result.try({
    try: () => keys.map(key => `${JSON.stringify(key)}:${Result.getOrThrowWith(
      serialize(descriptors[key].value, childPath(path, key), ancestors), error => refusal = error
    )}`),
    catch: cause => cause === refusal && refusal || canonicalInputError(cause)
  });
  return `{${items.join(",")}}`;
});

/** Pure, invocation-local traversal; no JSON accessor is invoked. */
export const canonicalJsonStringify = (value: unknown): Result.Result<string, CanonicalJsonError> =>
  inspect(() => serialize(value, "$", new Set())).pipe(Result.flatMap(result => result));

/** Construct the encoder before traversing input, as the native call does. */
export const canonicalJsonBytes = (value: unknown): Result.Result<Uint8Array<ArrayBuffer>, CanonicalJsonError> => inspect(() => {
  const encoder = new TextEncoder(), encode = encoder.encode;
  return canonicalJsonStringify(value).pipe(Result.flatMap(text => inspect(() => encode.call(encoder, text))));
}).pipe(Result.flatMap(result => result));
